import React, { useState, useEffect } from 'react';
import { userService, authService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const UserManagementTab = () => {
  const { user: currentUser, setUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [processingUserId, setProcessingUserId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getAllUsers();
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      if (error.response?.status === 403) {
        setMessage({ type: 'error', text: 'Access denied: Admin privileges required' });
      } else {
        setMessage({ type: 'error', text: 'Failed to load users' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    const confirmMessage = currentRole === 'admin'
      ? 'Are you sure you want to remove admin privileges from this user?'
      : 'Are you sure you want to grant admin privileges to this user?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setProcessingUserId(userId);
      await userService.updateUserRole(userId, newRole);
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));

      // If the affected user is the current user, refresh their session data
      if (userId === currentUser?.id) {
        try {
          const response = await authService.getCurrentUser();
          setUser(response.data.user);
          
          if (newRole === 'user') {
            setMessage({ 
              type: 'success', 
              text: 'Your admin privileges have been removed. This page will no longer be accessible.' 
            });
            // Redirect after a delay
            setTimeout(() => {
              window.location.href = '/';
            }, 2000);
            return;
          }
        } catch (err) {
          console.error('Failed to refresh user session:', err);
        }
      }

      setMessage({ 
        type: 'success', 
        text: `User role updated to ${newRole}. The user may need to refresh their browser to see changes.` 
      });
    } catch (error) {
      console.error('Error updating user role:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update user role';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setProcessingUserId(null);
    }
  };

  const clearMessage = () => {
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  useEffect(() => {
    if (message.text) clearMessage();
  }, [message]);

  const adminUsers = users.filter(u => u.role === 'admin');
  const regularUsers = users.filter(u => u.role !== 'admin');

  // Filter users based on search query
  const filterUsers = (userList) => {
    if (!searchQuery.trim()) return userList;
    const query = searchQuery.toLowerCase();
    return userList.filter(u => 
      u.name?.toLowerCase().includes(query) || 
      u.email?.toLowerCase().includes(query)
    );
  };

  const filteredAdminUsers = filterUsers(adminUsers);
  const filteredRegularUsers = filterUsers(regularUsers);

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-800 mb-2">User Management</h3>
      <p className="text-sm text-gray-600 mb-4">
        Manage user roles and administrative privileges. Admin users have access to Configuration settings.
      </p>

      {/* Search Filter */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <svg 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Message Display */}
      {message.text && (
        <div className={`mb-4 p-3 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-100 text-green-800 border border-green-200' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="space-y-6 max-h-[calc(100vh-400px)] overflow-y-auto pr-2">
          {/* Admin Users Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <h4 className="text-md font-semibold text-gray-800">
                Administrators ({filteredAdminUsers.length})
              </h4>
            </div>
            
            {filteredAdminUsers.length > 0 ? (
              <div className="space-y-2">
                {filteredAdminUsers.map(user => (
                  <UserCard 
                    key={user.id} 
                    user={user} 
                    onRoleChange={handleRoleChange}
                    isProcessing={processingUserId === user.id}
                  />
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500 italic py-2">
                {searchQuery ? 'No admin users match your search' : 'No admin users found'}
              </div>
            )}
          </div>

          {/* Regular Users Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <h4 className="text-md font-semibold text-gray-800">
                Regular Users ({filteredRegularUsers.length})
              </h4>
            </div>
            
            {filteredRegularUsers.length > 0 ? (
              <div className="space-y-2">
                {filteredRegularUsers.map(user => (
                  <UserCard 
                    key={user.id} 
                    user={user} 
                    onRoleChange={handleRoleChange}
                    isProcessing={processingUserId === user.id}
                  />
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500 italic py-2">
                {searchQuery ? 'No regular users match your search' : 'No regular users found'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const UserCard = ({ user, onRoleChange, isProcessing }) => {
  const isAdmin = user.role === 'admin';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          {/* Avatar */}
          {user.avatarUrl ? (
            <img 
              src={user.avatarUrl} 
              alt={user.name || 'User avatar'} 
              className="w-10 h-10 rounded-full object-cover"
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          {/* Fallback Avatar - shown if no avatarUrl or image fails to load */}
          <div 
            className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center"
            style={{ display: user.avatarUrl ? 'none' : 'flex' }}
          >
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>

          {/* User Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h5 className="text-sm font-semibold text-gray-900 truncate">
                {user.name || 'Unknown User'}
              </h5>
              {isAdmin && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                  Admin
                </span>
              )}
            </div>
            <p className="text-xs text-gray-600 truncate">{user.email}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Joined {new Date(user.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Role Toggle Button */}
        <button
          onClick={() => onRoleChange(user.id, user.role)}
          disabled={isProcessing}
          className={`ml-3 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            isAdmin
              ? 'bg-red-100 text-red-700 hover:bg-red-200 border border-red-300'
              : 'bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-300'
          } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {isProcessing ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          ) : isAdmin ? (
            'Remove Admin'
          ) : (
            'Make Admin'
          )}
        </button>
      </div>
    </div>
  );
};

export default UserManagementTab;
